import React, { useMemo } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { PlanBadge } from "~/components/plan-badge";
import { ExternalLink } from "lucide-react";
import { OnboardingDashboard } from "~/components/ui/onboarding-dashboard";
import { ProfileCompletion } from "~/components/ui/profile-completion";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { MembershipPlan } from "~/lib/schemas";
import { getOnboardingStatus } from "../edit-profile/index";
import { getInvoicesForCoachData } from "../invoices/index";
import { cn } from "~/lib/cn";
import {
  PLAN_FEATURES_PUBLIC,
  type GhlFeatureKey,
} from "~/lib/ghl/plan-features-public";

export const Route = createFileRoute("/coach/_backend/business/")({
  component: RouteComponent,
});

function RouteComponent() {
  const coach = useCoachPrivateProfileRequired();

  const { data: onboardingStatus } = useQuery({
    queryKey: ["coach", "onboarding-status"],
    queryFn: () => getOnboardingStatus(),
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["coach-invoices"],
    queryFn: () => getInvoicesForCoachData(),
  });

  const invoiceStats = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonthPaid = invoices.filter((inv) =>
      inv.paymentDate ? new Date(inv.paymentDate) >= startOfMonth : false,
    );
    const revenueThisMonth = thisMonthPaid.reduce(
      (sum, inv) => sum + inv.amount,
      0,
    );
    const sentCount = invoices.length;
    const paidCount = invoices.filter((i) => i.status === "paid").length;
    return { revenueThisMonth, sentCount, paidCount };
  }, [invoices]);

  return (
    <div className="mx-auto w-full max-w-screen-xl px-4 py-8">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-text-primary text-2xl md:text-3xl">
            Business Dashboard
          </h1>
          {coach.membershipPlan !== MembershipPlan.Standard && (
            <PlanBadge membershipPlan={coach.membershipPlan} />
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link to="/coach/membership-plans">
            <Button variant="fancy">Upgrade</Button>
          </Link>
          <Link to="/coach/invoices">
            <Button variant="outline">Create Invoice</Button>
          </Link>
        </div>
      </div>

      {/* Onboarding / Profile Health */}
      {onboardingStatus && !onboardingStatus.onboarding_completed_at && (
        <div className="mb-8">
          <OnboardingDashboard status={onboardingStatus} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* 1. Visibility & Profile Health */}
        <section className="col-span-1 lg:col-span-2">
          <div className="space-y-4">
            <h3 className="text-text-primary text-lg font-medium">
              Visibility & Profile Health
            </h3>
            <div className="grid grid-cols-1 gap-6">
              <div className="grid grid-cols-1 items-start gap-6 @container">
                <div className="@lg:flex @lg:items-start @lg:gap-6">
                  <div className="min-w-0 flex-1">
                    <ProfileCompletion
                      className="w-full"
                      coach={coach}
                      profile={coach.profile}
                      details={coach.details}
                      mainOffer={coach.mainOffer}
                      dialSize={160}
                    />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3 @lg:mt-0 @lg:flex-col @lg:flex-shrink-0">
                    <Link to="/coach/edit-profile/profile">
                      <Button size="sm">Edit Profile</Button>
                    </Link>
                    <Link
                      to="/$username"
                      params={{ username: coach.username }}
                      target="_blank"
                    >
                      <Button variant="outline" size="sm">
                        View Public Profile
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* KPIs (placeholders where data not yet available) */}
                <div className="grid grid-cols-2 gap-4 @lg:grid-cols-4">
                  <Kpi title="Profile Views (Week)" value="–" />
                  <Kpi title="Profile Views (Month)" value="–" />
                  <Kpi
                    title="Resources Shared"
                    value={String(coach.resources?.length ?? 0)}
                  />
                  <Kpi title="Resource Page Views" value="–" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Plan & Subscription Overview */}
        <section className="col-span-1">
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Plan & Subscription</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-text-secondary">Current Plan</span>
                  <PlanBadge membershipPlan={coach.membershipPlan} />
                </div>
                <Link to="/coach/membership-plans">
                  <Button size="sm">Upgrade</Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                <span>Benefits used</span>
                <span className="text-right">Coming soon</span>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>

      {/* 3. Earnings Summary */}
      <section className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Earnings Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 @container md:grid-cols-3">
              <Kpi
                title="This Month’s Revenue"
                value={`$${(invoiceStats.revenueThisMonth / 100).toFixed(2)}`}
              />
              <Kpi
                title="Invoices Sent"
                value={String(invoiceStats.sentCount)}
              />
              <Kpi
                title="Invoices Paid"
                value={String(invoiceStats.paidCount)}
              />
            </div>
            <div className="mt-4 flex gap-3">
              <Link to="/coach/invoices">
                <Button>Create Invoice</Button>
              </Link>
              <Link to="/coach/invoices">
                <Button variant="outline">Withdraw Earnings</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 4. Business at a Glance */}
      <section className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Business at a Glance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Kpi title="Active Clients" value="–" />
              <Kpi title="Upcoming Renewals" value="–" />
              <Kpi title="Ending Engagements" value="–" />
            </div>
            <div className="mt-4">
              <Link to="/coach/messages">
                <Button variant="outline">Open Client Management</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* 5. Leads & Prospect Management */}
      <section className="mt-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Core Marketing Tools</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {getEnabledGhlLinks(
                  coach.membershipPlan,
                  coach.ghlLocationId || null,
                ).map((item) => (
                  <OutboundLink
                    key={item.label}
                    href={item.href}
                    label={item.label}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Lead Volume</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <Kpi title="Total Contacts" value="–" />
                <Kpi title="New (30 Days)" value="–" />
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 6. Community Engagement */}
      <section className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Community Engagement</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Most recent community post: coming soon
            </div>
            <Link to="/blog">
              <Button>Go to Community Hub</Button>
            </Link>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function Kpi({ title, value }: { title: string; value: string }) {
  return (
    <div className="rounded-md border bg-white p-4">
      <div className="text-text-tertiary text-xs">{title}</div>
      <div className="font-heading text-text-primary mt-1 text-xl tabular-nums">
        {value}
      </div>
    </div>
  );
}

function OutboundLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      className={cn(
        "ring-brand-100 inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm transition",
        "hover:bg-gray-50 hover:ring-2",
      )}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="mr-2">{label}</span>
      <ExternalLink className="h-4 w-4 opacity-70" />
    </a>
  );
}

function getEnabledGhlLinks(
  plan: MembershipPlan,
  locationId: string | null,
): { label: string; href: string; key: GhlFeatureKey }[] {
  const features = PLAN_FEATURES_PUBLIC[plan];
  const base = "https://app.dreamcoachmatch.com";
  const loc = locationId ? `/v2/location/${locationId}` : "";

  const links: {
    key: GhlFeatureKey;
    label: string;
    href: string;
    enabled: boolean;
  }[] = [
    {
      key: "pipelines",
      label: "Pipelines",
      href: `${base}${loc}/pipelines`,
      enabled: features.pipelines,
    },
    {
      key: "conversations",
      label: "Conversations",
      href: `${base}${loc}/conversations`,
      enabled: features.conversations,
    },
    {
      key: "emailMarketing",
      label: "Email",
      href: `${base}${loc}/marketing/campaigns/email`,
      enabled: features.emailMarketing,
    },
    {
      key: "automations",
      label: "Automations",
      href: `${base}${loc}/automations`,
      enabled: features.automations,
    },
    {
      key: "funnels",
      label: "Funnels",
      href: `${base}${loc}/sites/funnels`,
      enabled: features.funnels,
    },
    {
      key: "forms",
      label: "Forms",
      href: `${base}${loc}/forms`,
      enabled: features.forms,
    },
  ];

  return links.filter((l) => l.enabled).map(({ enabled: _e, ...rest }) => rest);
}
